import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Error as ErrorIcon, Link as LinkIcon, PlayArrow, PriceChange } from '@mui/icons-material';
import { IngestionBrandInfo, IngestionJobType, TriggerIngestionPayload } from '../../services/integrationApi';
import { tokens } from '../../theme/tokens';
import { ChipInput, NumberField } from '../ui';
import { validateUrl } from '../../utils/inputValidators';

export interface BrandTriggerResult {
  executionMethod?: 'cloud' | 'local' | 'cloud_run';
  runCommand?: string;
  jobId?: string;
  message?: string;
}

interface BrandTriggerDialogProps {
  open: boolean;
  brand: IngestionBrandInfo | null;
  onClose: () => void;
  onTrigger: (brandId: string, payload: TriggerIngestionPayload) => void;
  isPending: boolean;
  triggerResult?: BrandTriggerResult | null;
  errorMessage?: string | null;
  errorJobId?: string | null;
  onClearResult?: () => void;
}

/** ChipInput validator for a product URL: only http(s) addresses are scraped. */
export function validateProductUrl(value: string): string | null {
  return validateUrl(value);
}

/**
 * The URLs a run will scrape: exactly the entries whose chip is not red. The chip
 * validator, the count and the payload all go through `validateProductUrl`.
 */
export function parseProductUrls(text: string): string[] {
  const urls = splitUrlDraft(text).filter((u) => validateProductUrl(u) === null);
  return Array.from(new Set(urls));
}

/** The pasted-URL draft as one entry per URL, kept whether or not it is valid. */
export function splitUrlDraft(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export const BrandTriggerDialog: React.FC<BrandTriggerDialogProps> = ({
  open,
  brand,
  onClose,
  onTrigger,
  isPending,
  triggerResult,
  errorMessage,
  errorJobId,
  onClearResult,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [type, setType] = useState<IngestionJobType>('full_scrape');
  const [limit, setLimit] = useState<string>('');
  const [category, setCategory] = useState<string>('all');
  const [hostImages, setHostImages] = useState(true);
  const [region, setRegion] = useState<'US' | 'CA'>('US');
  const [urlsText, setUrlsText] = useState('');

  const parsedUrls = useMemo(() => parseProductUrls(urlsText), [urlsText]);
  const hasTargetUrls = parsedUrls.length > 0;

  const handleSubmit = () => {
    if (!brand) return;
    onTrigger(brand.id, {
      type: hasTargetUrls ? 'full_scrape' : type,
      limit: hasTargetUrls ? undefined : limit ? parseInt(limit, 10) : undefined,
      category: hasTargetUrls || category === 'all' ? undefined : category,
      hostImages,
      region,
      ...(hasTargetUrls ? { urls: parsedUrls } : {}),
    });
  };

  const handleClose = () => {
    onClearResult?.();
    setUrlsText('');
    onClose();
  };

  if (!brand) return null;

  if (triggerResult?.executionMethod === 'local' && triggerResult.runCommand) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Run Locally: {brand.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'warning.light',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <ErrorIcon color="warning" />
            <Box>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Cloud Run unavailable - run locally
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {triggerResult.message || 'Run the command below to start the scraper locally.'}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Run this command in your terminal:
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.900',
                color: 'grey.100',
                borderRadius: 1,
                fontFamily: tokens.font.mono,
                fontSize: '0.85rem',
                overflow: 'auto',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.800' },
              }}
              onClick={() => navigator.clipboard.writeText(triggerResult.runCommand || '')}
              title="Click to copy"
            >
              {triggerResult.runCommand}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Click to copy to clipboard
            </Typography>
          </Box>

          {triggerResult.jobId && (
            <Typography variant="body2" color="text.secondary">
              Job ID: <code>{triggerResult.jobId}</code> - progress will appear in the UI once the scraper starts.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Run Ingestion: {brand.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {errorMessage && (
          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {errorMessage}
            </Typography>
            {errorJobId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Running Job ID: <code>{errorJobId}</code>
              </Typography>
            )}
          </Box>
        )}
        <FormControl fullWidth>
          <InputLabel>Region</InputLabel>
          <Select
            value={region}
            label="Region"
            onChange={(e) => setRegion(e.target.value as 'US' | 'CA')}
          >
            <MenuItem value="US">US (USD)</MenuItem>
            <MenuItem value="CA">Canada (CAD)</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={hasTargetUrls}>
          <InputLabel>Job Type</InputLabel>
          <Select
            value={hasTargetUrls ? 'full_scrape' : type}
            label="Job Type"
            onChange={(e) => setType(e.target.value as IngestionJobType)}
          >
            <MenuItem value="full_scrape">Full Scrape</MenuItem>
            <MenuItem value="incremental_scrape">Incremental (New Only)</MenuItem>
            <MenuItem value="rescrape_missing">Re-scrape Missing Images</MenuItem>
            <MenuItem value="price_refresh">Price Refresh Only</MenuItem>
          </Select>
        </FormControl>

        {type !== 'price_refresh' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Product URLs (optional)</Typography>
            <ChipInput
              aria-label="Product URLs (optional)"
              value={splitUrlDraft(urlsText)}
              onChange={(next) => setUrlsText(next.join('\n'))}
              validate={validateProductUrl}
              placeholder="https://www.example.com/product/abc"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {hasTargetUrls
                ? `${parsedUrls.length} URL${parsedUrls.length === 1 ? '' : 's'} will be scraped (full PDP). Category/limit ignored.`
                : 'Paste one or more product page URLs (newline or comma separated) to scrape only those pages.'}
            </Typography>
          </Box>
        )}

        {type === 'price_refresh' && !hasTargetUrls && (
          <Typography variant="body2" color="text.secondary">
            Compares current source prices against staged and canonical products. Images are not re-hosted.
          </Typography>
        )}

        {type !== 'price_refresh' && !hasTargetUrls && (
          <>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {brand.categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <NumberField
              label="Limit (optional)"
              integer
              min={1}
              adornment="products"
              value={limit}
              onChange={setLimit}
              helperText="Max products to process (leave empty for all)"
              size="medium"
              sx={{ width: '100%' }}
            />
          </>
        )}

        {type !== 'price_refresh' && (
          <FormControlLabel
            control={<Switch checked={hostImages} onChange={(e) => setHostImages(e.target.checked)} />}
            label="Host images on our infrastructure"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isPending}
          startIcon={
            isPending ? (
              <CircularProgress size={16} />
            ) : hasTargetUrls ? (
              <LinkIcon />
            ) : type === 'price_refresh' ? (
              <PriceChange />
            ) : (
              <PlayArrow />
            )
          }
        >
          {isPending
            ? 'Starting...'
            : hasTargetUrls
              ? `Scrape ${parsedUrls.length} URL${parsedUrls.length === 1 ? '' : 's'}`
              : type === 'price_refresh'
                ? 'Start Price Refresh'
                : 'Start Ingestion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandTriggerDialog;
